import React, { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Send, Sparkles, User } from "lucide-react";
import { useAuth } from "@/lib/auth";
import UsageUpsellBanner from "@/components/UsageUpsellBanner";

const SUGGESTIONS = [
  "Which customer cohort is most at risk of churn?",
  "What's the biggest revenue opportunity across our data right now?",
  "Are there supply chain anomalies I should escalate?",
  "Summarize today's insights for the board.",
];

export default function Assistant() {
  const { user } = useAuth();
  const [sessionId] = useState(() => `sess-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (text) => {
    const t = (text ?? input).trim();
    if (!t || sending) return;
    setInput("");
    const userMsgId = Date.now();
    const aiMsgId = userMsgId + 1;
    setMessages(m => [...m, { role: "user", content: t, id: userMsgId }]);
    setMessages(m => [...m, { role: "assistant", content: "", id: aiMsgId, streaming: true }]);
    setSending(true);

    try {
      const token = localStorage.getItem("dara_token");
      const res = await fetch(`${API}/assistant/chat/stream`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ session_id: sessionId, message: t }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        // Process complete SSE events (terminated by "\n\n")
        let idx;
        while ((idx = buffer.indexOf("\n\n")) >= 0) {
          const event = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          for (const line of event.split("\n")) {
            if (!line.startsWith("data: ")) continue;
            try {
              const payload = JSON.parse(line.slice(6));
              if (payload.chunk) {
                setMessages(m => m.map(x => x.id === aiMsgId
                  ? { ...x, content: x.content + payload.chunk } : x));
              }
              if (payload.error) throw new Error(payload.error);
              if (payload.done) {
                setMessages(m => m.map(x => x.id === aiMsgId ? { ...x, streaming: false } : x));
              }
            } catch (e) {
              if (e.message?.startsWith("HTTP")) throw e;
            }
          }
        }
      }
      setMessages(m => m.map(x => x.id === aiMsgId ? { ...x, streaming: false } : x));
    } catch (err) {
      setMessages(m => m.filter(x => x.id !== aiMsgId));
      toast.error("DARA unreachable. Retry.");
    }
    setSending(false);
  };

  return (
    <div className="flex flex-col h-full" data-testid="assistant-page">
      {/* header */}
      <div className="border-b border-zinc-900 p-6 flex items-center justify-between">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">MODULE · DARA ASSISTANT</div>
          <h1 className="font-heading font-black text-3xl tracking-tighter uppercase mt-1">Talk to your data.</h1>
        </div>
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-[#FFB000]">
          <Sparkles className="w-3 h-3" /> DARA ANALYST · LIVE
        </div>
      </div>
      <div className="px-6 pt-4"><UsageUpsellBanner /></div>

      {/* messages */}
      <div className="flex-1 overflow-auto p-6 space-y-4" data-testid="assistant-messages">
        {messages.length === 0 && (
          <div className="max-w-2xl mx-auto py-12 text-center">
            <Sparkles className="w-8 h-8 text-[#FFB000] mx-auto mb-4" />
            <h3 className="font-heading font-bold text-2xl mb-2">Ask anything about {user?.company}.</h3>
            <p className="text-sm text-zinc-500 mb-8">DARA has full context of your data sources, insights and revenue pipeline.</p>
            <div className="grid sm:grid-cols-2 gap-2">
              {SUGGESTIONS.map(s => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  data-testid={`suggest-${s.slice(0, 10)}`}
                  className="text-left p-3 border border-zinc-800 hover:border-[#FFB000]/50 hover:text-[#FFB000] font-mono text-[11px] text-zinc-400 transition-colors"
                >
                  <span className="text-[#FFB000] mr-2">▸</span>{s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map(m => (
          <div key={m.id} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`}>
            {m.role === "assistant" && (
              <div className="w-8 h-8 border border-[#FFB000]/30 bg-[#FFB000]/10 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-4 h-4 text-[#FFB000]" />
              </div>
            )}
            <div className={`max-w-[75%] p-4 ${m.role === "user" ? "bg-zinc-900 text-white" : "panel"}`}>
              <div className="whitespace-pre-wrap text-sm leading-relaxed font-body">
                {m.content}
                {m.streaming && <span className="inline-block w-2 h-4 ml-0.5 bg-[#FFB000] align-middle animate-pulse" />}
              </div>
            </div>
            {m.role === "user" && (
              <div className="w-8 h-8 border border-zinc-800 bg-zinc-900 flex items-center justify-center flex-shrink-0">
                <User className="w-4 h-4 text-zinc-400" />
              </div>
            )}
          </div>
        ))}

        {sending && messages.every(m => !m.streaming) && (
          <div className="flex gap-3">
            <div className="w-8 h-8 border border-[#FFB000]/30 bg-[#FFB000]/10 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-[#FFB000] animate-pulse" />
            </div>
            <div className="panel p-4">
              <span className="font-mono text-xs text-zinc-500">DARA is analyzing<span className="cursor-blink">▋</span></span>
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* input — extra right padding (desktop) + bottom padding (mobile) so the
          fixed Emergent badge in the bottom-right never covers the SEND button.
          Badge is ~210×60px positioned bottom-4 right-4. */}
      <form
        onSubmit={(e) => { e.preventDefault(); send(); }}
        className="border-t border-zinc-900 p-4 pr-6 md:pr-56 pb-20 md:pb-4 flex gap-3"
      >
        <input
          data-testid="assistant-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask about revenue, churn, anomalies, forecasts…"
          disabled={sending}
          className="flex-1 bg-black border border-zinc-800 px-4 py-3 font-mono text-sm text-white focus:outline-none focus:border-[#FFB000]"
        />
        <button
          type="submit"
          disabled={sending || !input.trim()}
          data-testid="assistant-send-btn"
          className="bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-5 hover:bg-[#E59E00] transition-colors flex items-center gap-2 disabled:opacity-50"
        >
          <Send className="w-4 h-4" /> SEND
        </button>
      </form>
    </div>
  );
}
